import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem,
  Box, Typography, Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { PAYMENT_METHOD, getPaymentMethodOptions } from '../../../utils/Enums';
import { formatMoney } from '../../../utils/currency';

const EditPaymentStatusModal = ({ open, onClose, onSave, statement }) => {
  const { t } = useTranslation();

  const [paymentData, setPaymentData] = useState({
    paymentStatus: 'partial', // Primer item del select de estado de pago
    paymentAmount: '',
    paymentDate: dayjs(),
    paymentMethod: PAYMENT_METHOD.CASH, // Usar directamente el enum
    notes: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (statement && open) {
      setPaymentData({
        paymentStatus: 'partial', // Primer item del select de estado de pago
        paymentAmount: '',
        paymentDate: dayjs(),
        paymentMethod: PAYMENT_METHOD.CASH, // Usar directamente el enum
        notes: ''
      });
    }
  }, [statement, open]);

  const handleFieldChange = (field, value) => {
    setPaymentData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Si se selecciona "Pago Total", establecer el monto como el total del estado de cuenta
      if (field === 'paymentStatus' && value === 'paid') {
        updated.paymentAmount = statement?.totalAmount || 0;
      }
      
      // Si se selecciona "Pago Parcial", limpiar el monto para que el usuario lo ingrese
      if (field === 'paymentStatus' && value === 'partial') {
        updated.paymentAmount = '';
      }
      
      return updated;
    });
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateFields = () => {
    const newErrors = {};

    if (!paymentData.paymentStatus) {
      newErrors.paymentStatus = t('payment_status_required');
    }

    if (paymentData.paymentStatus === 'partial' && paymentData.paymentAmount <= 0) {
      newErrors.paymentAmount = t('payment_amount_required');
    }

    if (!paymentData.paymentDate) {
      newErrors.paymentDate = t('payment_date_required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    // Verificar que todos los campos requeridos estén completos
    if (!paymentData.paymentStatus) return false;
    if (!paymentData.paymentDate) return false;
    if (paymentData.paymentStatus === 'partial' && (!paymentData.paymentAmount || paymentData.paymentAmount <= 0)) return false;
    if (!paymentData.paymentMethod) return false;
    return true;
  };

  const handleSave = () => {
    if (validateFields()) {
      // Preparar datos según la estructura que espera el backend
      const paidAmountValue = paymentData.paymentStatus === 'paid' 
        ? statement.totalAmount 
        : parseFloat(paymentData.paymentAmount.replace(/,/g, '.')) || 0;
      
      const paymentStatusData = {
        paidAmount: paidAmountValue, // Enviar como número (double)
        notes: paymentData.notes || '',
        paymentMethod: paymentData.paymentMethod || 'CASH',
        isCompletePayment: paymentData.paymentStatus === 'paid', // true si es pago total, false si es parcial
        // Fecha elegida en el formulario (antes se ignoraba y se guardaba la fecha de hoy)
        paymentDate: dayjs(paymentData.paymentDate).format('YYYY-MM-DDTHH:mm:ss')
      };
      
      console.log('EditPaymentStatusModal - Sending payment data:', paymentStatusData);
      
      onSave(paymentStatusData);
      onClose();
    }
  };

  const handleClose = () => {
    setPaymentData({
      paymentStatus: 'partial', // Primer item del select de estado de pago
      paymentAmount: '',
      paymentDate: dayjs(),
      paymentMethod: PAYMENT_METHOD.CASH, // Usar directamente el enum
      notes: ''
    });
    setErrors({});
    onClose();
  };

  const formatCurrency = (amount) => {
    return formatMoney(amount);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getIcon('settings')}
        {t('edit_payment_status')}
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Información del estado de cuenta */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                {t('statement_information')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('client')}:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {statement?.clientName}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('total_amount')}:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(statement?.totalAmount || 0)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('paid_amount')}:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(statement?.paidAmount || 0)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('pending_amount')}:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                  {formatCurrency(statement?.pendingAmount || 0)}
                </Typography>
              </Box>
            </Grid>

            {/* Configuración de pago */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                {t('payment_configuration')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.paymentStatus}>
                <InputLabel>{t('payment_status')}</InputLabel>
                <Select
                  value={paymentData.paymentStatus}
                  label={t('payment_status')}
                  onChange={e => handleFieldChange('paymentStatus', e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    {t('select_payment_status')}
                  </MenuItem>
                  <MenuItem value="partial">{t('partial_payment')}</MenuItem>
                  <MenuItem value="paid">{t('paid')}</MenuItem>
                </Select>
                {errors.paymentStatus && <Typography variant="caption" color="error">{errors.paymentStatus}</Typography>}
              </FormControl>
            </Grid>

            {paymentData.paymentStatus === 'paid' && (
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('pending_amount')}
                  fullWidth
                  type="text"
                  value={formatCurrency(statement?.pendingAmount || 0)}
                  disabled
                  helperText={t('pending_amount_info')}
                />
              </Grid>
            )}

            {paymentData.paymentStatus === 'partial' && (
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('payment_amount')}
                  fullWidth
                  type="text"
                  value={paymentData.paymentAmount}
                  onChange={e => {
                    let inputValue = e.target.value;
                    
                    // Convertir punto (.) a coma (,) para formato uruguayo
                    inputValue = inputValue.replace(/\./g, ',');
                    
                    // Solo permitir números y una coma decimal
                    inputValue = inputValue.replace(/[^0-9,]/g, '');
                    
                    // Solo permitir una coma decimal
                    const parts = inputValue.split(',');
                    if (parts.length > 2) {
                      inputValue = parts[0] + ',' + parts[1];
                    }
                    
                    // Limitar a máximo 2 decimales después de la coma
                    if (parts.length === 2 && parts[1].length > 2) {
                      inputValue = parts[0] + ',' + parts[1].substring(0, 2);
                    }
                    
                    // Convertir coma a punto para parseFloat (JavaScript usa punto para decimales)
                    const valueForParse = inputValue.replace(/,/g, '.');
                    let value = parseFloat(valueForParse) || 0;
                    const maxAmount = statement?.pendingAmount || 0;
                    
                    // Si excede el saldo pendiente, corregir automáticamente
                    if (value > maxAmount) {
                      value = maxAmount;
                      inputValue = maxAmount.toFixed(2).replace(/\./g, ',');
                    }
                    
                    handleFieldChange('paymentAmount', inputValue);
                  }}
                  placeholder="0,00"
                  error={!!errors.paymentAmount}
                  helperText={errors.paymentAmount || `${t('max_amount')}: ${formatCurrency(statement?.pendingAmount || 0)}`}
                  InputProps={{
                    startAdornment: '$'
                  }}
                />
              </Grid>
            )}

            {paymentData.paymentStatus !== 'unpaid' && (
              <>

                <Grid item xs={12} md={6}>
                  <DatePicker
                    label={t('payment_date')}
                    value={paymentData.paymentDate}
                    onChange={(newValue) => handleFieldChange('paymentDate', newValue)}
                    slotProps={{ 
                      textField: { 
                        fullWidth: true, 
                        error: !!errors.paymentDate,
                        helperText: errors.paymentDate
                      } 
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('payment_method')}</InputLabel>
                    <Select
                      value={paymentData.paymentMethod}
                      label={t('payment_method')}
                      onChange={e => handleFieldChange('paymentMethod', e.target.value)}
                    >
                      {getPaymentMethodOptions().map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                label={t('notes')}
                fullWidth
                multiline
                rows={3}
                value={paymentData.notes}
                onChange={e => handleFieldChange('notes', e.target.value)}
                placeholder={t('payment_notes_placeholder')}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('cancel')}</Button>
        <Button onClick={handleSave} variant="contained" disabled={!isFormValid()}>{t('save')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPaymentStatusModal;
